import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { APPPOINTMENT_BLOCK_FULFILLMENT_FEATURE } from "constants/feature-flags";
import { selectHasRegions } from "features/auth/auth-slice";
import { eventsApi } from "services/events";
import { useDeleteScheduleMutation } from "services/schedules";
import { useGetStaffQuery, useLazyGetStaffQuery } from "services/staff";

import useAppointments from "../hooks/use-appointments.hook";
import {
  endPatientCreateAppointmentMode,
  selectClinicsFilterValues,
  selectPatientCreateAppointmentModeValues
} from "../scheduler-slice";

export default function usePatientCreateAppointmentMode() {
  const flags = useFlags();
  const isAppointmentBlockFulfillEnabled =
    flags[APPPOINTMENT_BLOCK_FULFILLMENT_FEATURE];
  const dispatch = useAppDispatch();

  const { patient, returnToPath } = useAppSelector(
    selectPatientCreateAppointmentModeValues
  );
  const hasRegions = useAppSelector(selectHasRegions);
  const clinicsFilterIds = useAppSelector(selectClinicsFilterValues);

  const { setupAppointmentModal, setEditingForm, refetchEvents } =
    useAppointments();

  const [deleteScheduleMutation] = useDeleteScheduleMutation();

  // ----- Stable args & skip logic to prevent duplicate calls -----
  const staffArgs = useMemo(
    () => ({
      clinicIds: hasRegions ? clinicsFilterIds : undefined
    }),
    [hasRegions, clinicsFilterIds]
  );

  const shouldSkipStaff =
    !hasRegions ||
    (Array.isArray(clinicsFilterIds) && clinicsFilterIds.length === 0);

  // Pull from cache; avoid rerenders by selecting only what we need.
  const { data: availableStaff = [] } = useGetStaffQuery(staffArgs, {
    skip: shouldSkipStaff,
    // keep the cache around even if args change briefly
    refetchOnMountOrArgChange: false
  });

  // Lazy fetch ONLY if we must resolve a missing staff member at modal-open time.
  const [triggerStaff] = useLazyGetStaffQuery();

  const staffById = useMemo(() => {
    const map = new Map<number, (typeof availableStaff)[number]>();
    for (const s of availableStaff) map.set(s.id, s);
    return map;
  }, [availableStaff]);

  const setupPatientCreateAppointmentModeModal = useCallback(
    async (appointment: any) => {
      // Resolve staff member: prefer provided object, then cache, then (rarely) lazy fetch.
      let resolvedStaff =
        appointment.staffMember ??
        (appointment.staffMemberId
          ? staffById.get(appointment.staffMemberId)
          : undefined);

      if (!resolvedStaff && !shouldSkipStaff && appointment.staffMemberId) {
        // One-off fetch to hydrate cache only when necessary
        const res = await triggerStaff(staffArgs, true);
        const list = ("data" in res && res.data) || [];
        resolvedStaff = list.find(
          (s: any) => s.id === appointment.staffMemberId
        );
      }

      const enrichedAppointment = {
        ...appointment,
        patient,
        staffMember: resolvedStaff
      };

      setupAppointmentModal(
        enrichedAppointment,
        "add",
        () => {
          dispatch(endPatientCreateAppointmentMode());
          dispatch(eventsApi.util.invalidateTags(["Events"]));
          refetchEvents();
          setEditingForm(false);

          if (
            appointment.scheduled_block_id &&
            isAppointmentBlockFulfillEnabled
          ) {
            deleteScheduleMutation({
              scheduleId: appointment.scheduled_block_id,
              instance_start_date: appointment.block_start_date
            });
          }
        },
        () => setEditingForm(false)
      );
    },
    [
      patient,
      setupAppointmentModal,
      dispatch,
      refetchEvents,
      setEditingForm,
      deleteScheduleMutation,
      isAppointmentBlockFulfillEnabled,
      staffById,
      triggerStaff,
      staffArgs,
      shouldSkipStaff
    ]
  );

  return {
    setupPatientCreateAppointmentModeModal,
    patientCreateAppointmentMode: patient,
    returnToPath
  };
}
